import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame, Loader2, Package, Search, ShieldCheck, TrendingUp, Truck } from "lucide-react";
import { api, fmt } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }),
};

export default function HomePage() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [aeResults, setAeResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const resultsRef = useRef(null);

  useEffect(() => {
    api.get("/storefront/home").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const search = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data: r } = await api.get("/search/marketplace", { params: { q: query } });
      setResults(r.results);
      setAeResults({ items: r.aliexpress || [], live: r.aliexpress_live, error: r.aliexpress_error });
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch {}
    setSearching(false);
  };

  if (!data)
    return (
      <div className="flex min-h-[70vh] items-center justify-center" data-testid="home-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
      </div>
    );

  const { hero, trending, bestsellers, categories } = data;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(234,179,8,0.12),transparent_55%)]" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-8 lg:grid-cols-2 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <p className="eyebrow mb-5">Trend intelligence + direct fulfilment</p>
            <h1 className="font-heading text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {hero ? hero.name : "Tomorrow's bestsellers."}
              <span className="mt-2 block text-amber-400">Before everyone else.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-400">
              We scan Google Trends, Amazon and eBay around the clock, import what's about to blow up,
              and ship it direct from the manufacturer to your door — no middlemen, no markup chains.
            </p>
            <form onSubmit={search} className="mt-8 flex max-w-lg gap-2" data-testid="home-search-form">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  data-testid="home-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search trending products across marketplaces…"
                  className="input-dark !rounded-full !py-3 !pl-11"
                />
              </div>
              <button type="submit" data-testid="home-search-btn" disabled={searching} className="btn-gold !px-5 !py-3 text-sm disabled:opacity-60">
                {searching ? <Loader2 size={16} className="animate-spin" /> : "Search"}
              </button>
            </form>
            <p className="mt-2 text-[11px] text-slate-600">
              Searches TrendDrop + TikTok Shop, AliExpress, eBay & Amazon listings{" "}
              <span className="text-amber-500/70">(marketplace prices MOCKED until API keys connect)</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/shop" className="btn-gold" data-testid="hero-shop-now-btn">
                Shop trending now <ArrowRight size={17} />
              </Link>
              <Link to="/shop?badge=bestseller" className="btn-ghost" data-testid="hero-bestsellers-btn">
                Best sellers
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              {[
                { icon: TrendingUp, label: "Live trend scanning" },
                { icon: Truck, label: "Direct-from-factory shipping" },
                { icon: ShieldCheck, label: "30-day returns" },
                { icon: Package, label: "18,000+ orders shipped" },
              ].map((s, i) => (
                <span key={i} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-medium text-slate-300">
                  <s.icon size={14} className="text-amber-400" /> {s.label}
                </span>
              ))}
            </div>
          </motion.div>
          {hero && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
              <Link to={`/product/${hero.id}`} data-testid="hero-product-link" className="group relative block">
                <div className="gold-glow relative overflow-hidden rounded-3xl border border-white/10">
                  <img src={hero.images[0]} alt={hero.name} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-black/90 to-transparent p-6">
                    <div>
                      <p className="text-xs font-mono uppercase tracking-widest text-amber-400">Featured drop</p>
                      <p className="font-heading text-xl font-semibold text-white">{hero.name}</p>
                    </div>
                    <p className="font-heading text-2xl font-bold text-amber-400">{fmt(hero.price)}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* TRENDING */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="eyebrow mb-2">Spiking right now</p>
            <h2 className="font-heading text-2xl font-semibold text-white sm:text-3xl">Trending this week</h2>
          </div>
          <Link to="/shop?badge=trending" className="flex items-center gap-1.5 text-sm font-medium text-amber-400 hover:text-amber-300" data-testid="trending-view-all">
            View all <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {trending.map((p, i) => (
            <motion.div key={p.id} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
              <ProductCard product={p} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="border-y border-white/[0.06] bg-[#0D0D11]">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-8">
          <p className="eyebrow mb-2">Browse</p>
          <h2 className="mb-8 font-heading text-2xl font-semibold text-white sm:text-3xl">Shop by category</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {categories.map((c) => (
              <Link
                key={c.name}
                to={`/shop?category=${encodeURIComponent(c.name)}`}
                data-testid={`category-tile-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="card-surface group p-6 transition-all duration-300 hover:border-amber-400/40"
              >
                <p className="font-heading text-lg font-semibold text-white group-hover:text-amber-300">{c.name}</p>
                <p className="mt-1 text-xs text-slate-500">{c.count} products · trend score {c.avg_trend}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BESTSELLERS */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="eyebrow mb-2">Proven winners</p>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-semibold text-white sm:text-3xl">
              <Flame size={24} className="text-amber-400" /> Best sellers
            </h2>
          </div>
          <Link to="/shop?badge=bestseller" className="flex items-center gap-1.5 text-sm font-medium text-amber-400 hover:text-amber-300" data-testid="bestsellers-view-all">
            View all <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {bestsellers.map((p, i) => (
            <motion.div key={p.id} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
              <ProductCard product={p} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* SEARCH RESULTS */}
      {results && (
        <section ref={resultsRef} className="mx-auto max-w-7xl px-4 py-14 sm:px-8" data-testid="home-search-results">
          <p className="eyebrow mb-2">Marketplace results for &ldquo;{query}&rdquo;</p>
          <h2 className="mb-8 font-heading text-2xl font-semibold text-white sm:text-3xl">
            {results.length} product{results.length === 1 ? "" : "s"} found
          </h2>
          {results.length === 0 ? (
            <p className="text-slate-500" data-testid="search-no-results">No matches — try "drone", "LED", "earbuds"…</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((p) => (
                <div key={p.id} className="card-surface flex gap-4 p-4 transition-colors hover:border-amber-400/40" data-testid={`search-result-${p.id}`}>
                  <img src={p.image} alt={p.name} className="h-24 w-24 shrink-0 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading font-semibold text-white">{p.name}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{p.category}</p>
                    <div className="mt-1.5 flex items-baseline gap-2">
                      <span className="font-heading text-lg font-bold text-amber-400">{fmt(p.price)}</span>
                      <span className="text-[11px] text-slate-600">at TrendDrop</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {p.marketplaces.map((m) => (
                        <span
                          key={m.name}
                          data-testid={`search-mp-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}-${p.id}`}
                          title={m.price ? `~${fmt(m.price)} on ${m.name} (MOCKED)` : `Not listed on ${m.name}`}
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                            m.listed ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-white/10 text-slate-500"
                          }`}
                        >
                          {m.name}{m.price ? ` · ${fmt(m.price)}` : ""}
                        </span>
                      ))}
                    </div>
                    <Link to={`/product/${p.id}`} data-testid={`view-details-${p.id}`} className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-amber-400 hover:text-amber-300">
                      View details <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* LIVE ALIEXPRESS RESULTS */}
          {aeResults && (
            <div className="mt-10">
              <div className="mb-5 flex items-center gap-3">
                <h3 className="font-heading text-xl font-semibold text-white">Live from AliExpress</h3>
                {aeResults.live ? (
                  <span className="rounded-full border border-emerald-400/30 bg-emerald-400/15 px-2.5 py-0.5 text-[10px] font-bold text-emerald-300" data-testid="ae-live-badge">LIVE API</span>
                ) : (
                  <span className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] text-slate-500" data-testid="ae-offline-badge">
                    live feed unavailable{aeResults.error ? ` — ${aeResults.error}` : ""}
                  </span>
                )}
              </div>
              {aeResults.items.length > 0 && (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                  {aeResults.items.map((p) => (
                    <div key={p.id} className="card-surface overflow-hidden" data-testid={`ae-result-${p.id}`}>
                      <img src={p.image} alt={p.title} loading="lazy" className="aspect-square w-full bg-white/5 object-cover" />
                      <div className="p-4">
                        <p className="line-clamp-2 text-sm font-medium text-white">{p.title}</p>
                        <div className="mt-2 flex items-baseline gap-2">
                          <p className="font-heading text-lg font-bold text-amber-400" data-testid={`ae-price-${p.id}`}>${p.price}</p>
                          {p.source_price != null && (
                            <span className="text-[11px] text-slate-600 line-through">${p.source_price}</span>
                          )}
                        </div>
                        <a href={p.buy_url} target="_blank" rel="noreferrer sponsored" data-testid={`ae-buy-${p.id}`} className="btn-gold mt-3 w-full !px-3 !py-2 text-xs">
                          Buy on AliExpress
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* NEW ARRIVALS */}
      {data.newest?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8" data-testid="new-arrivals-section">
          <p className="eyebrow mb-2">Just imported</p>
          <h2 className="mb-8 font-heading text-2xl font-semibold text-white sm:text-3xl">New arrivals</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {data.newest.map((p, i) => (
              <motion.div key={p.id} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <ProductCard product={p} />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* MULTI-CHANNEL STRIP */}
      <section className="border-t border-white/[0.06] bg-[#0D0D11]">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-12 text-center sm:px-8">
          <p className="eyebrow">One catalog, everywhere</p>
          <h2 className="max-w-2xl font-heading text-2xl font-semibold text-white sm:text-3xl">
            Also selling on <span className="text-sky-400">eBay</span> and <span className="text-rose-400">TikTok Shop</span>
          </h2>
          <p className="max-w-xl text-sm text-slate-400">
            Every order — from any channel — is auto-fulfilled direct from the manufacturer and tracked in one place.
          </p>
        </div>
      </section>
    </div>
  );
}
